import { FilterOption, PreloadedFilter, PreloadedItemsFilters } from '../../../../core/api/ports';

/**
 * Lógica pura de los filtros de "Listado de items precargados" (`ModalListItems`): campaña, tipo de cultivo y
 * departamento. Separada para testearla.
 */

/** Selección de cada filtro. Una lista vacía = todos. */
export type PreloadedFiltersSelection = Record<PreloadedFilter, FilterOption[]>;

/** Todos los filtros, en el orden en que se muestran. */
export const PRELOADED_FILTERS: PreloadedFilter[] = [
    PreloadedFilter.Campaign,
    PreloadedFilter.CropType,
    PreloadedFilter.Department
];

/** Un `Record<PreloadedFilter, T>` con el mismo valor (nuevo por filtro) en cada clave. */
export const byPreloadedFilter = <T>(build: (filter: PreloadedFilter) => T): Record<PreloadedFilter, T> => ({
    [PreloadedFilter.Campaign]: build(PreloadedFilter.Campaign),
    [PreloadedFilter.Department]: build(PreloadedFilter.Department),
    [PreloadedFilter.CropType]: build(PreloadedFilter.CropType)
});

/** Selección inicial de campaña: la primera opción, que backend manda como la más reciente. Sin opciones → `[]`. */
export const preselectMostRecent = (options: FilterOption[]): FilterOption[] =>
    options && options.length > 0 ? [options[0]] : [];

const idsOf = (options: FilterOption[] | null | undefined): string[] => (options || []).map(option => option.id);

/** Selección de la UI → filtros del puerto (ids en el orden de la selección; vacío = todos). */
export const toItemsFilters = (selected: Partial<PreloadedFiltersSelection> | null | undefined): PreloadedItemsFilters => ({
    campaigns: idsOf(selected ? selected[PreloadedFilter.Campaign] : undefined),
    departments: idsOf(selected ? selected[PreloadedFilter.Department] : undefined),
    cropTypes: idsOf(selected ? selected[PreloadedFilter.CropType] : undefined)
});

/** Agrega `option` a la selección si no estaba (por `id`). */
export const addOption = (selected: FilterOption[], option: FilterOption): FilterOption[] =>
    (selected || []).some(item => item.id === option.id) ? selected : [...(selected || []), option];

/** Quita `option` de la selección (por `id`). */
export const removeOption = (selected: FilterOption[], option: FilterOption): FilterOption[] =>
    (selected || []).filter(item => item.id !== option.id);

/**
 * Lotes marcados que siguen en el resultado nuevo (intersección por `id`). Devuelve los objetos del resultado nuevo,
 * así la tabla los reconoce como propios.
 */
export const keepSelectedItems = <T extends { id: number | string }>(selected: T[], nextList: T[]): T[] => {
    const selectedIds = new Set((selected || []).map(item => item.id));
    return (nextList || []).filter(item => selectedIds.has(item.id));
};
